import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/base/ScrollToTop";
import Breadcrumb from "@/components/base/Breadcrumb";
import {
  OrganizationSchema,
  WebPageSchema,
  BreadcrumbListSchema,
} from "@/components/base/StructuredData";
import { aboutInfo } from "@/mocks/novel";

const CONTACT_FORM_URL = "https://readdy.ai/api/form/d9qmd9me3oq4jqumtn00";

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotField = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotField && honeypotField.value.trim() !== "") {
      setFormStatus("success");
      return;
    }

    const formData = new FormData(form);
    const textarea = formData.get("message") as string;
    if (textarea && textarea.length > 500) {
      setFormStatus("error");
      setFormError("留言内容不能超过500个字符。");
      return;
    }

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== "phone_alt") {
        params.append(key, value as string);
      }
    });

    try {
      const response = await fetch(CONTACT_FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === "OK") {
        setFormStatus("success");
        form.reset();
      } else {
        setFormStatus("error");
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormError(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setFormStatus("error");
      setFormError("网络错误，请检查网络连接后重试。");
    }
  };

  return (
    <>
      <ScrollToTop />
      <OrganizationSchema />
      <WebPageSchema
        title="关于我们 - 书香阁 | 品牌介绍、口碑与联系方式"
        description="书香阁是一个专注于中文网络小说阅读与推荐的优质平台。了解我们的品牌故事、读者口碑与联系方式。"
        url="https://shuxiangge.com/about"
      />
      <BreadcrumbListSchema
        items={[
          { name: "首页", url: "https://shuxiangge.com/" },
          { name: "关于我们", url: "https://shuxiangge.com/about" },
        ]}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-24">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-5xl mx-auto">
              <Breadcrumb items={[{ label: "关于我们", to: undefined }]} />

              {/* Hero */}
              <section className="text-center py-10 md:py-14">
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">
                  关于书香阁
                </h1>
                <p className="text-foreground-500 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
                  {aboutInfo.mission}
                </p>
              </section>

              {/* Brand Intro */}
              <section className="py-8 md:py-12">
                <div className="flex flex-col lg:flex-row gap-10 items-center">
                  <div className="w-full lg:w-1/2">
                    <img
                      src="https://readdy.ai/api/search-image?query=Modern%20Chinese%20reading%20space%20interior%20with%20warm%20lighting%2C%20wooden%20bookshelves%20filled%20with%20books%2C%20cozy%20reading%20nook%2C%20traditional%20Chinese%20aesthetic%20blended%20with%20contemporary%20design%2C%20peaceful%20atmosphere%2C%20soft%20natural%20light%20streaming%20through%20windows&width=800&height=500&seq=about-brand&orientation=landscape"
                      alt="书香阁品牌形象"
                      className="w-full rounded-lg"
                    />
                  </div>
                  <div className="flex-1">
                    <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                      品牌介绍
                    </h2>
                    <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                      {aboutInfo.brandIntro}
                    </p>
                    <div className="grid grid-cols-2 gap-4 mt-6">
                      {aboutInfo.reputation.map((item) => (
                        <div
                          key={item.label}
                          className="p-4 rounded-lg bg-background-100 border border-background-200/70 text-center"
                        >
                          <p className="font-heading text-xl md:text-2xl font-bold text-primary-500">
                            {item.value}
                          </p>
                          <p className="text-xs text-foreground-500 mt-1">{item.label}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </section>

              {/* Reputation */}
              <section className="py-10 md:py-14">
                <div className="text-center mb-10">
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                    我们的口碑
                  </h2>
                  <p className="text-foreground-500 text-sm mt-2">
                    来自读者与行业的认可
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {[
                    {
                      title: "千万读者信赖",
                      content: "平台累计服务超过580万读者，日活跃用户突破百万，是中文网络小说领域最具影响力的阅读平台之一。",
                      icon: "ri-heart-line",
                    },
                    {
                      title: "海量正版内容",
                      content: "与超过500家出版社和原创文学平台建立合作关系，收录12万余部正版授权作品，确保读者享受优质的阅读体验。",
                      icon: "ri-shield-check-line",
                    },
                    {
                      title: "极致阅读体验",
                      content: "自主研发的阅读引擎支持多种阅读模式，智能推荐算法精准匹配读者喜好，98.6%的用户给予好评。",
                      icon: "ri-thumb-up-line",
                    },
                  ].map((item) => (
                    <div
                      key={item.title}
                      className="p-6 rounded-lg bg-background-100 border border-background-200/70 text-center"
                    >
                      <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 mx-auto mb-4">
                        <i className={`${item.icon} text-2xl`}></i>
                      </div>
                      <h3 className="font-heading text-base font-semibold text-foreground-900 mb-3">
                        {item.title}
                      </h3>
                      <p className="text-sm text-foreground-500 leading-relaxed">
                        {item.content}
                      </p>
                    </div>
                  ))}
                </div>
              </section>

              {/* Contact */}
              <section className="py-10 md:py-14">
                <div className="text-center mb-10">
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                    联系我们
                  </h2>
                  <p className="text-foreground-500 text-sm mt-2">
                    有任何问题或建议？我们随时倾听
                  </p>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                  <div>
                    <div className="space-y-5">
                      {[
                        { icon: "ri-mail-line", label: "电子邮箱", value: aboutInfo.contact.email },
                        { icon: "ri-wechat-line", label: "微信公众号", value: aboutInfo.contact.wechat },
                        { icon: "ri-weibo-line", label: "官方微博", value: aboutInfo.contact.weibo },
                        { icon: "ri-phone-line", label: "客服电话", value: aboutInfo.contact.phone },
                        { icon: "ri-map-pin-line", label: "公司地址", value: aboutInfo.contact.address },
                      ].map((item) => (
                        <div key={item.label} className="flex items-start gap-4">
                          <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500 shrink-0">
                            <i className={`${item.icon} text-lg`}></i>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-foreground-800">{item.label}</p>
                            <p className="text-sm text-foreground-500">{item.value}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-background-100 rounded-lg border border-background-200/70 p-6 md:p-8">
                    <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-6">
                      在线留言
                    </h3>
                    {formStatus === "success" ? (
                      <div className="text-center py-10">
                        <div className="w-16 h-16 flex items-center justify-center rounded-full bg-accent-100 text-accent-500 mx-auto mb-4">
                          <i className="ri-check-line text-3xl"></i>
                        </div>
                        <p className="text-foreground-800 font-medium">提交成功！</p>
                        <p className="text-sm text-foreground-500 mt-2">感谢您的留言，我们会尽快回复。</p>
                        <button
                          onClick={() => setFormStatus("idle")}
                          className="mt-6 px-5 py-2 text-sm font-medium bg-background-200 text-foreground-700 rounded-full hover:bg-background-300 transition-colors"
                        >
                          继续留言
                        </button>
                      </div>
                    ) : (
                      <form onSubmit={handleSubmit} data-readdy-form="">
                        {formStatus === "error" && formError && (
                          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-600">
                            {formError}
                          </div>
                        )}
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-foreground-700 mb-1">
                              姓名 <span className="text-red-400">*</span>
                            </label>
                            <input
                              type="text"
                              name="name"
                              required
                              className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-200 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                              placeholder="请输入您的姓名"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-foreground-700 mb-1">
                              邮箱 <span className="text-red-400">*</span>
                            </label>
                            <input
                              type="email"
                              name="email"
                              required
                              className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-200 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                              placeholder="请输入您的邮箱"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-foreground-700 mb-1">
                              主题
                            </label>
                            <input
                              type="text"
                              name="subject"
                              className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-200 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                              placeholder="请输入留言主题"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-foreground-700 mb-1">
                              留言内容 <span className="text-red-400">*</span>
                            </label>
                            <textarea
                              name="message"
                              required
                              maxLength={500}
                              rows={4}
                              className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-200 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                              placeholder="请输入您的留言（不超过500字）"
                            ></textarea>
                          </div>
                          <input
                            type="text"
                            name="phone_alt"
                            className="honeypot-field"
                            tabIndex={-1}
                            autoComplete="off"
                            aria-hidden="true"
                            readOnly
                          />
                          <button
                            type="submit"
                            className="w-full px-6 py-3 text-sm font-semibold bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
                          >
                            提交留言
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                </div>
              </section>

              {/* Map */}
              <section className="py-10 md:py-14">
                <div className="rounded-lg overflow-hidden border border-background-200/70">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3060.0532143478064!2d116.472679!3d39.916345!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x35f052b2e5e5f5e5%3A0x5e5e5e5e5e5e5e5e!2z5YyX5Lqs5biC5pyd6Ziz5Yy65paH5YyW5Yib5oSP5Li65Lq65ZWG5Zut!5e0!3m2!1szh-CN!2scn!4v1690000000000!5m2!1szh-CN!2scn"
                    width="100%"
                    height="400"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="书香阁地址"
                    aria-label="书香阁办公地址地图"
                  ></iframe>
                </div>
              </section>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}